<template>
    <layout :breadcrumbs="breadcrumbs">
        <template slot="header">
            <h1 class="text-center">
                Select Make & Model
            </h1>
        </template>

        <b-row class="d-sm-none mobile-header">
            <b-col>
                <h1 v-if="selectedFragment === 'make'" class="text-center">
                    Select Make
                </h1>
                <h1
                    v-else-if="selectedFragment === 'model'"
                    class="text-center"
                >
                    Select Model
                </h1>
            </b-col>
        </b-row>

        <b-row id="shop-by-make">
            <b-col v-if="showMakes" cols="12" md="3" class="make-column">
                <b-form-select v-model="category">
                    <option value="all">
                        All
                    </option>
                    <option value="american">
                        American
                    </option>
                    <option value="asian">
                        Asian
                    </option>
                    <option value="european">
                        European
                    </option>
                    <option value="luxury">
                        Luxury
                    </option>
                </b-form-select>
                <div
                    v-for="make in availableMakes"
                    :key="make.id"
                    class="make-block d-flex justify-content-between align-items-center my-3"
                    @click="onMakeSelect(make)"
                >
                    <make-icon :make="make" :selected="isMakeSelected(make)" />
                </div>
            </b-col>
            <b-col v-if="showModels" cols="12" md="9">
                <div v-if="selectedMakeName" class="h-100">
                    <div
                        v-if="isLoading"
                        class="d-flex flex-column h-100 justify-content-center text-center"
                    >
                        <loading-spinner :size="60" class="mr-auto ml-auto" />
                    </div>

                    <div v-else>
                        <div
                            v-if="yearOptions != null && yearOptions.length > 1"
                            class="d-flex my-1 my-sm-0 justify-content-between justify-content-md-end"
                        >
                            <div
                                v-if="selectedMakeName"
                                class="d-md-none make-header d-flex mb-0 justify-content-center flex-column"
                            >
                                <div>
                                    {{ selectedMakeName }}
                                    <b-link @click="backToMakes">
                                        <small>(change)</small>
                                    </b-link>
                                </div>
                            </div>
                            <year-selector
                                v-model="selectedYear"
                                :options="yearOptions"
                                @change="changeYear"
                            />
                        </div>

                        <div v-for="bodyType in bodyTypes" :key="bodyType.name">
                            <div class="body-style-header position-relative">
                                <div
                                    class="ico-block d-flex flex-column text-center position-absolute"
                                >
                                    <body-style-icon
                                        :id="bodyType.name"
                                        class="d-none d-sm-block"
                                    />
                                    <span class="body-style-title">
                                        {{ bodyType.name | toUpper }}
                                    </span>
                                </div>
                            </div>
                            <div
                                class="model-block d-flex justify-content-start flex-wrap"
                            >
                                <div
                                    v-for="model in bodyType.models"
                                    :key="model.id"
                                    class="d-flex flex-column justify-content-center align-items-center p-2 model"
                                    @click="onModelSelect(model)"
                                >
                                    <div
                                        class="model-wrapper d-block flex-column justify-content-center"
                                    >
                                        <b-img
                                            v-if="model.profileImageUrl"
                                            :src="model.profileImageUrl"
                                            fluid
                                        />
                                        <div
                                            v-else
                                            class="missing-image-wrapper h-100 d-flex justify-content-center align-items-center"
                                        >
                                            <body-style-icon
                                                :id="bodyStyleOrDefault(model)"
                                            />
                                        </div>
                                    </div>
                                    <div
                                        class="d-flex flex-column justify-content-center align-items-center text-block"
                                    >
                                        <span class="model-name">
                                            {{ model.name }}
                                        </span>
                                        <span class="model-price">
                                            starting at
                                            {{ model.msrp | numeral("$0,0") }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-else
                    class="d-flex flex-column h-100 text-center select-make"
                >
                    <h3>Please select a Make</h3>
                </div>
            </b-col>
        </b-row>
    </layout>
</template>

<script>
import { get, sync, call } from "vuex-pathify";
import _ from "lodash";
import MakeIcon from "Modules/UsedListings/components/MakeIcon";
import BodyStyleIcon from "Components/BodyStyleIcon";
import LoadingSpinner from "Components/LoadingSpinner";
import makeCategories from "../utils/MakeCategories";
import Layout from "../components/Layout";
import YearSelector from "Modules/ShopBy/Make/components/YearSelector";

export default {
    name: "ShopByMake",
    components: {
        YearSelector,
        LoadingSpinner,
        BodyStyleIcon,
        MakeIcon,
        Layout
    },

    filters: {
        toUpper(text) {
            return _.toUpper(text);
        }
    },

    props: {
        makeName: {
            type: String,
            required: false,
            default: null
        }
    },

    data() {
        return {
            selectedFragment: "make",
            category: "all",
            breadcrumbs: [
                {
                    text: "Shop By",
                    href: "/shop-by"
                },
                {
                    text: "Make & Model",
                    active: true
                }
            ],
            makeCategories
        };
    },

    computed: {
        selectedYear: sync("shopBy/selectedYear"),
        makes: get("shopBy/makes"),
        models: get("shopBy/modelBaseStyles"),
        isLoading: get("shopBy/loading"),
        selectedMakeName: get("shopBy/selectedMakeName"),
        selectedMake: get("shopBy/selectedMake"),
        yearOptions() {
            return _.get(this.selectedMake, "availableYears", []) || [];
        },
        availableMakes() {
            if (this.category === "all") {
                return this.makes;
            }

            return _.filter(this.makes, make => {
                return (
                    _.indexOf(
                        this.makeCategories[this.category],
                        _.toLower(make.name)
                    ) !== -1
                );
            });
        },
        bodyTypes() {
            const bodyTypesList = [];
            let bodyTypes = {};

            if (_.isNil(this.models)) {
                return bodyTypesList;
            }
            bodyTypes = _.chain(this.models)
                .filter(["core4Enabled", true])
                .filter(model => {
                    return _.get(model, "startingAt", 0) > 0;
                })
                .map(model => {
                    return {
                        id: model.id,
                        msrp: model.startingAt,
                        name: model.name,
                        bodyType: model.bodyType,
                        profileImageUrl: model.profileImageUrl,
                        core4Enabled: model.core4Enabled
                    };
                })
                .groupBy("bodyType")
                .value();

            _.forIn(bodyTypes, function(models, name) {
                const sortedModels = _.sortBy(models, function(model) {
                    return model.name.toLowerCase();
                });

                bodyTypesList.push({
                    name,
                    models: sortedModels
                });
            });

            return _.sortBy(bodyTypesList, ["name"]);
        },
        showMakes() {
            if (this.$mq === "sm" || this.$mq === "xs") {
                return this.selectedFragment === "make";
            }
            return true;
        },
        showModels() {
            if (this.$mq === "sm" || this.$mq === "xs") {
                return this.selectedFragment === "model";
            }

            return true;
        }
    },

    mounted() {
        if (_.isNil(this.makes) || this.makes.length === 0) {
            this.getMakes("shopBy/getMakes").then(() => {
                if (this.makeName) {
                    const make = _.find(this.makes, ["name", this.makeName]);
                    this.onMakeSelect(make);
                }
            });
        } else if (this.makeName) {
            this.onMakeSelect(this.makeName);
        }

        if (!_.isNil(this.selectedMakeName)) {
            this.getStylesByMakeName({
                makeName: this.selectedMakeName,
                year: this.selectedYear
            });
        }
    },

    methods: {
        getMakes: call("shopBy/getMakes"),
        getStylesByMakeName: call("shopBy/getStylesByMakeName"),
        addFilter: call("shopBy/addFilter"),
        setSelectedYear: call("shopBy/setSelectedYear"),
        removeFilter: call("shopBy/removeFilter"),
        changeYear(value) {
            this.getStylesByMakeName({
                makeName: this.selectedMakeName,
                year: value
            });
        },
        onMakeSelect(make) {
            this.selectedFragment = "model";
            this.addFilter({ name: "makes", value: make.name });
            this.setSelectedYear(make.defaultYear);
            this.getStylesByMakeName({
                makeName: make.name,
                year: make.defaultYear
            });
            window.scrollTo(0, 0);
        },
        onModelSelect(model) {
            this.$router.push({
                name: "trims-body-type",
                params: {
                    makeName: this.selectedMakeName,
                    modelId: model.id.toString(),
                    bodyType: model.bodyType
                }
            });
        },
        isMakeSelected(make) {
            return this.selectedMakeName === make.name;
        },
        bodyStyleOrDefault(model) {
            if (_.isNil(model.bodyType)) {
                return "sedan";
            }

            return model.bodyType;
        },
        backToMakes() {
            this.removeFilter("makes");
            this.selectedFragment = "make";
            window.scrollTo(0, 0);
        }
    }
};
</script>

<style lang="scss">
.mobile-header {
    h1 {
        font-size: 24px;
    }
}

#shop-by-make {
    .make-header {
        font-size: 14px;
        color: $primary-blue;
    }

    select {
        border: none;
        border-bottom: 1px solid $muted-gray;
    }

    .make-column {
        border-right: 10px solid $very-light-gray;
        height: calc(100vh - 100px);
        overflow-x: auto;

        .make-block {
            cursor: pointer;
        }
    }

    @include media-breakpoint-down(sm) {
        .make-column {
            height: auto;
            overflow: hidden;
        }
    }

    .body-style-header {
        height: 15px;
        z-index: 10;
        border-bottom: solid 1px $dark-gray;

        .ico-block {
            top: -12px;
            margin: 20px 20px 20px 40px;
            padding: 0 5px;
            background: $white;

            i {
                font-size: px2rem(32);
                color: $dark-gray;
            }

            .body-style-title {
                font-size: 12px;
                line-height: 13px;
                color: $dark-gray;
            }
        }

        @include media-breakpoint-up(md) {
            height: 35px;

            .ico-block {
                top: 0;

                .body-style-title {
                    font-size: 10px;
                }
            }
        }
    }

    .select-make {
        margin-top: 40%;
        opacity: 0.6;
    }

    .model-block {
        .model {
            cursor: pointer;
            width: 50%;

            .model-wrapper {
                max-width: 200px;
                height: 130px;
                overflow: hidden;
            }

            i {
                font-size: 3.2rem;
            }

            img {
                max-width: 100%;
                transition: max-width 200ms;
                margin: 0 auto;
            }

            .text-block {
                text-align: center;
                position: relative;
                bottom: 20px;

                .model-name {
                    font-size: px2rem(13);
                }
                .model-price {
                    font-size: px2rem(11);
                }
            }

            @include media-breakpoint-up(sm) {
                width: 33%;
            }

            @include media-breakpoint-up(md) {
                width: 33%;

                img {
                    max-width: 90%;
                }

                &:hover {
                    img {
                        max-width: 100%;
                    }

                    .text-block {
                        .model-name {
                            color: $primary-blue;
                            font-weight: bold;
                        }
                    }
                }
            }

            @include media-breakpoint-up(lg) {
                width: 25%;
            }
        }

        @include media-breakpoint-up(md) {
            margin: 10px 0 20px;
        }
    }
}
</style>
